import React from "react";
import AlertDialog from "../components/AlertDialog";
import SimpleSnackbar from "../components/SimpleSnackbar";

const BaseLayout = ({ children }) => {
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertTitle, setAlertTitle] = React.useState("");
  const [alertMessage, setAlertMessage] = React.useState("");

  const [snackOpen, setSnackOpen] = React.useState(false);
  const [snackText, setSnackText] = React.useState("");
  const [connectionSnackOpen, setConnectionSnackOpen] = React.useState(false);
  const [connectionSnackText, setConnectionSnackText] = React.useState("");

  const isNativeCapacitor =
    typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();

  const handleAlertClose = (value) => {
    setAlertOpen(false);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackOpen(false);
  };

  React.useEffect(() => {
    if (!isNativeCapacitor) {
      return;
    }

    let isCancelled = false;
    let probeController = null;

    const setConnectionWarning = (message) => {
      if (isCancelled) {
        return;
      }
      setConnectionSnackText(message);
      setConnectionSnackOpen(true);
    };

    const clearConnectionWarning = () => {
      if (isCancelled) {
        return;
      }
      setConnectionSnackOpen(false);
      setConnectionSnackText("");
    };

    const checkConnection = async () => {
      if (isCancelled) {
        return;
      }

      if (!navigator.onLine) {
        setConnectionWarning("No internet connection. Please check your Wi-Fi or data connection.");
        return;
      }

      try {
        if (probeController) {
          probeController.abort();
        }

        const controller = new AbortController();
        probeController = controller;
        const timeoutId = setTimeout(() => controller.abort(), 6000);
        const started = Date.now();

        const appProbeUrl = `https://app.alltimely.com/data/settings.json?t=${Date.now()}`;

        // Probe the primary app host directly.
        const appProbeResponse = await fetch(appProbeUrl, {
          method: "GET",
          cache: "no-store",
          signal: controller.signal,
        });

        if (!appProbeResponse.ok) {
          let hasGeneralInternet = false;
          try {
            const internetProbeResponse = await fetch(
              `https://www.gstatic.com/generate_204?t=${Date.now()}`,
              {
                method: "GET",
                cache: "no-store",
                mode: "no-cors",
                signal: controller.signal,
              },
            );

            // no-cors requests are opaque; receiving a response at all indicates basic internet reachability.
            hasGeneralInternet =
              internetProbeResponse.type === "opaque" ||
              internetProbeResponse.ok;
          } catch (fallbackError) {
            hasGeneralInternet = false;
          }

          clearTimeout(timeoutId);

          if (hasGeneralInternet) {
            setConnectionWarning(
              "Connection to app.alltimely.com is bad in this location. Move to a stronger Wi-Fi/data signal and try again.",
            );
          } else {
            setConnectionWarning(
              "Internet connection is bad. Move to a place with stronger Wi-Fi/data signal and try again.",
            );
          }
          return;
        }

        clearTimeout(timeoutId);

        const elapsedMs = Date.now() - started;
        if (elapsedMs > 4000) {
          setConnectionWarning(
            "Connection is very slow. You may experience delays or failed requests.",
          );
          return;
        }

        clearConnectionWarning();
      } catch (error) {
        setConnectionWarning(
          "Connection is unstable. Please verify your Wi-Fi or data connection.",
        );
      }
    };

    const onOnline = () => {
      checkConnection();
    };

    const onOffline = () => {
      setConnectionWarning("No internet connection. Please check your Wi-Fi or data connection.");
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    checkConnection();
    const intervalId = setInterval(checkConnection, 30000);

    return () => {
      isCancelled = true;
      if (probeController) {
        probeController.abort();
      }
      clearInterval(intervalId);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [isNativeCapacitor]);

  // make it globally available
  window.showAlert = (title, message) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  // make it globally available
  window.showSnack = (text) => {
    setSnackText(text);
    setSnackOpen(true);
  };
  
  window.showErrors = (title, errors) => {
    let str = title + ":\n";
    for (let error of errors) {
      str += error + "\n";
    }
    alert(str);
  };
  
  return (
    <>
      {children}

      <AlertDialog open={alertOpen} onClose={handleAlertClose} title={alertTitle} message={alertMessage} />
      <SimpleSnackbar
        open={snackOpen}
        text={snackText}
        handleClose={handleSnackClose}
      />
      <SimpleSnackbar
        open={connectionSnackOpen}
        text={connectionSnackText}
        autoHideDuration={null}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
    </>
  );
};

export default BaseLayout;
